import React, { useEffect, useState } from "react";
import { Link, useLocation, useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { resetCreativeState } from "../../../actions/app/creatives"
import { clearLandingPages } from "../../../actions/dcm/landingPages";
import { clearAllChangeLogCache } from "../../../utils/cacheUtils";
import { fetchUserProfilesService } from "../../../services/dcm/userProfiles";
import { fetchUserAdvertisers } from "../../../services/dcm/advertisers";
import { makeStyles } from "@material-ui/core/styles";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  MenuItem,
  Menu,
  Grid,
  ListItemText,
  CircularProgress
} from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";

import BartLogo from "./Images/BartLogo.png";
import CLogo from "./Images/c-favicon.png";

const useStyles = makeStyles((theme) => ({
  root: {
    // height: "10vh"
  },
  title: {
    paddingLeft: 16,
    paddingBottom: 6
  },
  bartLogo: {
    width: 60
  },
  caratLogo: {
    height: 50
  },
  logoLink: {
    color: "white",
    textDecoration: "none"
  },
  link: {
    color: "#000",
    textDecoration: "none"
  }
}));

const Header = () => {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [selectedTask, setSelectedTask] = React.useState("Assign Creatives");

  // Profile switcher state
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [profilesError, setProfilesError] = useState(false);
  const currentProfileId = typeof window !== "undefined"
    ? window.localStorage.getItem("googleProfileId")
    : null;
  const currentProfile = profiles.find((p) => String(p.profileId) === String(currentProfileId));

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = (task) => {
    if (task) setSelectedTask(task);
    setAnchorEl(null);
  };

  const openProfileMenu = (event) => {
    setProfileAnchorEl(event.currentTarget);
    if (profiles.length === 0 && !profilesLoading) {
      setProfilesLoading(true);
      setProfilesError(false);
      fetchUserProfilesService()
        .then((list) => setProfiles(Array.isArray(list) ? list : []))
        .catch(() => setProfilesError(true))
        .finally(() => setProfilesLoading(false));
    }
  };

  const closeProfileMenu = () => setProfileAnchorEl(null);

  // Surface the advertiser alongside the profile when the signed-in profile
  // maps to exactly one (QA: "logging into my profile with one advertiser
  // associated, I do not see the advertiser in the upper right corner").
  // Only shown for the single-advertiser case — a master admin profile has
  // dozens, where a name in the header would be meaningless. The endpoint is
  // scoped to the active profile, so there's no way to label the *other*
  // profiles in the menu without a BartAPI change.
  const [soleAdvertiserName, setSoleAdvertiserName] = useState("");

  useEffect(() => {
    if (location.pathname === "/login") return;
    if (!currentProfileId) return;

    let cancelled = false;
    fetchUserAdvertisers()
      .then((list) => {
        if (cancelled) return;
        setSoleAdvertiserName(
          Array.isArray(list) && list.length === 1 && list[0]
            ? list[0].name || ""
            : ""
        );
      })
      .catch(() => {
        if (!cancelled) setSoleAdvertiserName("");
      });

    return () => {
      cancelled = true;
    };
  }, [location.pathname, currentProfileId]);

  // Eagerly fetch the profile list once on mount so the header chip can
  // show the current profile's accountName immediately, without waiting
  // for the user to open the menu. Skip on /login where Header doesn't
  // render and we have no credentials yet.
  useEffect(() => {
    if (location.pathname === "/login") return;
    if (!currentProfileId) return;
    if (profiles.length > 0 || profilesLoading) return;
    setProfilesLoading(true);
    setProfilesError(false);
    fetchUserProfilesService()
      .then((list) => setProfiles(Array.isArray(list) ? list : []))
      .catch(() => setProfilesError(true))
      .finally(() => setProfilesLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const switchProfile = (newProfileId) => {
    if (String(newProfileId) === String(currentProfileId)) {
      closeProfileMenu();
      return;
    }
    // Clear caches that are keyed to the current profile so the next page
    // doesn't render stale data after reload.
    clearAllChangeLogCache();
    window.localStorage.setItem("googleProfileId", String(newProfileId));
    // Full reload — guarantees every component re-fetches under the new profile.
    window.location.reload();
  };

  useEffect(() => {
    switch (history.location.pathname) {
      case "/preloader":
        setSelectedTask("Creative Preloader");
        break;
      case "/name":
        setSelectedTask("Creative Naming");
        break;
      case "/changelog":
        setSelectedTask("Creative Log");
        break;
      default:
        setSelectedTask("Assign Creatives");
    }
  }, [history.location.pathname]);

  const logout = () => {
    dispatch(clearLandingPages());
    dispatch(resetCreativeState());

    localStorage.removeItem("googleAccessToken");
    localStorage.removeItem("googleAccessTokenExpiryDate");
    localStorage.removeItem("googleRefreshToken");
    localStorage.removeItem("googleProfileId");
    clearAllChangeLogCache();

    history.push("/login");
  };

  return (
    location.pathname !== "/login" && (
      <div className={classes.root}>
        <AppBar position="static" color="primary">
          <Toolbar
            style={{
              minHeight: "10vh"
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={4} style={{ textAlign: "left" }}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center"
                  }}
                >
                  <Link to="/">
                    <img
                      alt="Bart"
                      src={BartLogo}
                      className={classes.bartLogo}
                    />
                  </Link>
                  <Link to="/" className={classes.logoLink}>
                    <Typography variant="h6" className={classes.title}>
                      Bart
                    </Typography>
                  </Link>
                </div>
              </Grid>
              <Grid item xs={4} style={{ textAlign: "center" }}>
                <a href="https://www.carat.com">
                  <img alt="Carat" src={CLogo} className={classes.caratLogo} />
                </a>
              </Grid>
              <Grid container item xs={4} direction="row" justify="flex-end">
                <>
                  <Button
                    aria-controls="simple-menu"
                    aria-haspopup="true"
                    onClick={handleClick}
                    color="inherit"
                    disableElevation
                  >
                    {selectedTask}
                    <ArrowDropDownIcon />
                  </Button>
                  <Menu
                    id="simple-menu"
                    anchorEl={anchorEl}
                    keepMounted
                    open={Boolean(anchorEl)}
                    onClose={() => handleCloseMenu()}
                    transformOrigin={{
                      vertical: -45,
                      horizontal: "left"
                    }}
                  >
                    <MenuItem
                      onClick={() => handleCloseMenu("Assign Creatives")}
                    >
                      <Link to="/" className={classes.link}>
                        Assign Creatives
                      </Link>
                    </MenuItem>
                    <MenuItem
                      onClick={() =>
                        handleCloseMenu("Creative Naming")
                      }
                    >
                      <Link to="/name" className={classes.link}>
                        Creative Naming
                      </Link>
                    </MenuItem>
                    <MenuItem
                      onClick={() => handleCloseMenu("Creative Preloader")}
                    >
                      <Link to="/preloader" className={classes.link}>
                        Creative Preloader
                      </Link>
                    </MenuItem>
                    <MenuItem
                      onClick={() => handleCloseMenu("Creative Log")}
                    >
                      <Link to="/changelog" className={classes.link}>
                        Creative Log
                      </Link>
                    </MenuItem>
                  </Menu>
                  <Button
                    href="/help"
                    disableElevation
                    className={classes.logoLink}
                  >
                    Help
                  </Button>
                  <Button
                    onClick={openProfileMenu}
                    color="inherit"
                    disableElevation
                    startIcon={<AccountCircleIcon />}
                    title={
                      currentProfile
                        ? `${currentProfile.userName} • ${currentProfile.accountName}` +
                          (soleAdvertiserName ? ` • ${soleAdvertiserName}` : "")
                        : "Switch profile"
                    }
                  >
                    {currentProfile ? currentProfile.accountName : "Profile"}
                    {soleAdvertiserName ? ` • ${soleAdvertiserName}` : ""}
                    <ArrowDropDownIcon />
                  </Button>
                  <Menu
                    id="profile-menu"
                    anchorEl={profileAnchorEl}
                    keepMounted
                    open={Boolean(profileAnchorEl)}
                    onClose={closeProfileMenu}
                  >
                    {profilesLoading && (
                      <MenuItem disabled>
                        <CircularProgress size={16} style={{ marginRight: 8 }} />
                        Loading profiles…
                      </MenuItem>
                    )}
                    {profilesError && (
                      <MenuItem disabled>Could not load profiles</MenuItem>
                    )}
                    {!profilesLoading && !profilesError && profiles.length === 0 && (
                      <MenuItem disabled>No profiles available</MenuItem>
                    )}
                    {!profilesLoading && profiles.length > 0 && (
                      <MenuItem disabled style={{ opacity: 0.6, fontSize: "0.8rem" }}>
                        Switch profile
                      </MenuItem>
                    )}
                    {!profilesLoading &&
                      profiles.map((p) => {
                        const isCurrent = String(p.profileId) === String(currentProfileId);
                        return (
                          <MenuItem
                            key={p.profileId}
                            selected={isCurrent}
                            onClick={() => switchProfile(p.profileId)}
                          >
                            <ListItemText
                              primary={p.accountName}
                              secondary={
                                isCurrent
                                  ? `${p.userName} • current`
                                  : p.userName
                              }
                            />
                          </MenuItem>
                        );
                      })}
                  </Menu>
                  <Button onClick={logout} color="inherit" disableElevation>
                    Log Out
                  </Button>
                </>
              </Grid>
            </Grid>
          </Toolbar>
        </AppBar>
      </div>
    )
  );
};

export default Header;
